import { DatePipe, NgOptimizedImage } from '@angular/common'
import { ChangeDetectionStrategy, Component, computed, effect, inject, OnDestroy, OnInit, untracked } from '@angular/core'
import { toSignal } from '@angular/core/rxjs-interop'
import { MatButtonModule } from '@angular/material/button'
import { MatCardModule } from '@angular/material/card'
import { MatDialog } from '@angular/material/dialog'
import { MatIconModule } from '@angular/material/icon'
import { ActivatedRoute, Router, RouterModule } from '@angular/router'
import { AppState, AuthService } from '@auth0/auth0-angular'
import { combineLatest, startWith, Subscription } from 'rxjs'
import { LIB_LOGIN_CONFIG } from '../../../provider'
import { InfoCardAppearance, InfoCardComponent } from '../../cards/info-card.component'
import { LegalDialogComponent } from '../../navigation/legal-dialog/legal-dialog.component'
import { ZugangAnfordernDialogComponent } from '../zugang-anfordern-dialog/zugang-anfordern-dialog.component'

/** Was auf der Anmeldeseite unter der Ueberschrift steht, wenn Auth0 einen Fehler zurueckgibt. */
interface LoginHinweis {
  appearance: InfoCardAppearance
  icon: string
  titel: string
  text: string
  fussnote?: string
}

@Component({
  selector: 'lib-login',
  imports: [NgOptimizedImage, MatButtonModule, MatCardModule, MatIconModule, DatePipe, RouterModule, InfoCardComponent],
  templateUrl: './login.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: './login.component.scss',
})
export class LoginComponent implements OnInit, OnDestroy {
  private readonly dialog = inject(MatDialog)
  readonly auth = inject(AuthService)
  readonly router = inject(Router)
  readonly activatedRoute = inject(ActivatedRoute)
  readonly loginConfig = inject(LIB_LOGIN_CONFIG, { optional: true })

  readonly queryParams = toSignal(this.activatedRoute.queryParamMap)

  readonly now = new Date()

  /**
   * Auth0 liefert hier eine `GenericError` (auth0-spa-js): `message` ist die `error_description`,
   * also der Klartext aus dem Tenant. Im Template wird deshalb `message` gelesen und nicht das
   * Objekt selbst — sonst schreibt die Standard-`toString()` ein „Error: " davor, und genau das
   * stand bis 2026-08-28 als rote Zeile auf der Anmeldeseite.
   */
  private readonly fehler = toSignal(this.auth.error$)

  /**
   * Der haeufigste Fehler auf dieser Seite ist gar keiner: Wer sich gerade registriert hat, wird
   * von einer Auth0-Action abgewiesen, bis die E-Mail bestaetigt ist. Der Text dieser Action steht
   * im Auth0-Tenant und in keinem der beiden Repos, der Fehlercode ist das unspezifische
   * `access_denied` (das auch gesperrte Nutzer trifft). Deshalb wird der Klartext geprueft, und
   * zwar auf beide Haelften der Aussage — sonst faengt das Muster jede beliebige Absage mit dem
   * Wort „E-Mail" darin.
   */
  protected readonly emailUnbestaetigt = computed(() => {
    const text = this.fehler()?.message ?? ''
    return /e-?mail/i.test(text) && /verifizier|verify|bestätig|bestaetig|confirm/i.test(text)
  })

  /**
   * „Disable Sign Ups" steht in der Auth0-Connection, und das ist so gewollt: Zugaenge legt das
   * Sanitaetshaus in seiner Benutzerverwaltung an. Auth0 schickt in dem Fall die englische Zeile
   * „signup is disabled" zurueck, die bis 2026-09-04 als roter Fehlertext auf der Anmeldeseite
   * stand. Sie ist kein Fehler, sondern eine Auskunft — deshalb der ruhige Hinweis und der Dialog
   * mit dem Weg dorthin.
   */
  /**
   * Den Weg zu einem Zugang gibt es nur, wo ein Kunde seine Benutzer selbst anlegt (Portal). In der
   * internen Verwaltung registriert sich niemand, auch nicht auf Nachfrage: dort fehlen Knopf und
   * Dialog ganz (Entscheidung Tim 2026-10-06). Bis dahin zeigte sie einen Dialog mit „Schreiben Sie
   * uns an help@saniup.de" und einer Nachricht an „die Kollegin".
   */
  protected readonly zugangAnfordernMoeglich = !!this.loginConfig?.benutzerverwaltungPfad

  protected readonly registrierungGesperrt = computed(() => /signup\s+is\s+disabled/i.test(this.fehler()?.message ?? ''))

  protected readonly ueberschrift = computed(() => (this.emailUnbestaetigt() ? 'Fast geschafft!' : 'Herzlich Willkommen!'))

  protected readonly hinweis = computed<LoginHinweis | undefined>(() => {
    const fehler = this.fehler()
    if (!fehler) {
      return undefined
    }

    if (this.registrierungGesperrt() && !this.zugangAnfordernMoeglich) {
      return {
        appearance: 'info',
        icon: 'lock',
        titel: 'Keine Registrierung möglich',
        text: 'Die interne Verwaltung ist nur für Mitarbeitende von SaniUp.',
      }
    }

    if (this.registrierungGesperrt()) {
      return {
        appearance: 'info',
        icon: 'group_add',
        titel: 'Zugänge legt dein Sanitätshaus an',
        text: 'Du kannst dich hier nicht selbst registrieren. Eine Kollegin oder ein Kollege mit Zugang legt dein Konto an.',
        fussnote: 'Der Knopf „Noch keinen Zugang?" zeigt den Weg dorthin.',
      }
    }

    if (this.emailUnbestaetigt()) {
      return {
        appearance: 'info',
        icon: 'mark_email_unread',
        titel: 'Bestätige deine E-Mail-Adresse',
        text: 'Wir haben dir eine E-Mail geschickt. Klick auf den Link darin. Danach kannst du dich hier anmelden.',
        fussnote: 'Keine E-Mail bekommen? Sieh bitte auch im Spam-Ordner nach.',
      }
    }

    return {
      appearance: 'error',
      icon: 'error',
      titel: 'Die Anmeldung hat nicht geklappt',
      text: fehler.message,
      fussnote: 'Versuch es bitte erneut. Bleibt es dabei, hilft dir help@saniup.de weiter.',
    }
  })

  /**
   * Wer von Auth0 mit „signup is disabled" zurueckkommt, hat den Weg dorthin ueber ein Lesezeichen
   * oder die Auth0-Maske selbst genommen — der Knopf hier fuehrt ihn nicht mehr dahin. Er steht
   * also vor einer Absage, ohne zu wissen, was jetzt zu tun ist: der Dialog geht deshalb von
   * allein auf. Der Merker ist bewusst ein einfaches Feld, kein Signal — der Effekt darf den
   * Zustand, den er setzt, nicht als Abhaengigkeit lesen, sonst dreht er sich.
   */
  private zugangDialogGezeigt = false
  private readonly zugangDialogAnstossen = effect(() => {
    if (!this.registrierungGesperrt() || this.zugangDialogGezeigt || !this.zugangAnfordernMoeglich) {
      return
    }
    this.zugangDialogGezeigt = true
    untracked(() => this.zugangAnfordern())
  })

  private isAuthSubscription?: Subscription

  ngOnInit() {
    if (!this.loginConfig) {
      window.alert('LoginConfig is not provided')
    }

    // Redirect zu Face oder der zuletzt besuchten Seite sobald authentifiziert
    this.isAuthSubscription = combineLatest([this.auth.isAuthenticated$, this.auth.appState$.pipe(startWith({} as AppState))]).subscribe(
      ([isAuth, appState]) => {
        if (isAuth) {
          if (appState?.target) {
            // navigiert zur zuletzt besuchten Seite (@see canActivateAuthGuardFn)
            this.router.navigate([appState.target])
          } else {
            this.router.navigate([this.loginConfig?.internalRedirectUri])
          }
        }
      },
    )
  }

  ngOnDestroy() {
    this.isAuthSubscription?.unsubscribe()
  }

  login() {
    const target = this.queryParams()?.get('target') // speichert die Route auf die der Benutzer weitergeleitet werden soll
    this.auth.loginWithRedirect({ appState: { target } })
  }

  /**
   * Kein Umweg mehr ueber Auth0: die Connection hat „Disable Sign Ups", der Sprung dorthin endete
   * also zwangslaeufig mit „signup is disabled" und einer roten Zeile hier. Der Dialog beantwortet
   * dieselbe Frage sofort und richtig.
   */
  zugangAnfordern() {
    this.dialog.open(ZugangAnfordernDialogComponent, {
      width: '34rem',
      maxWidth: '95vw',
      autoFocus: 'first-heading',
    })
  }

  openLegalDialog() {
    this.dialog.open(LegalDialogComponent)
  }
}
